import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { AlertCircle, CheckCircle, Upload, Camera, FileText, Shield } from 'lucide-react';
import Webcam from 'react-webcam';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { verifyIdentity, checkLiveness } from '../../services/azureFaceService';
import { getApiBaseUrl } from '../../utils/apiConfig';
import axios from 'axios';

const VerificationCenter = ({ user, onVerificationComplete, onClose }) => {
  const [step, setStep] = useState('overview'); // overview, id_upload, liveness, review
  const [idDocument, setIdDocument] = useState(null);
  const [driversLicense, setDriversLicense] = useState(null);
  const [vehicleCertificate, setVehicleCertificate] = useState(null);
  const [livenessPhoto, setLivenessPhoto] = useState(null);
  const [webcamRef, setWebcamRef] = useState(null);
  const [verificationData, setVerificationData] = useState({
    idVerified: false,
    faceVerified: false,
    documentsVerified: false
  });
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const isDriver = user.role === 'driver';

  const handleCancel = () => {
    if (step === 'overview') {
      // If on first step, just close
      onClose();
    } else {
      // Show confirmation dialog
      setShowCancelConfirm(true);
    }
  };

  const confirmCancel = () => {
    toast({ 
      title: 'Verification Cancelled', 
      description: 'You can restart verification anytime from your profile'
    });
    onClose();
  };

  const handleFileUpload = (type, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'id') setIdDocument(reader.result);
        else if (type === 'license') setDriversLicense(reader.result);
        else if (type === 'certificate') setVehicleCertificate(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const captureLiveness = async () => {
    if (webcamRef) {
      const imageSrc = webcamRef.getScreenshot();
      setLivenessPhoto(imageSrc);
      
      toast({ title: 'Analyzing photo...', description: 'Please wait' });
      
      // Real Azure Face API liveness check
      const livenessResult = await checkLiveness(imageSrc);
      
      if (livenessResult.success && livenessResult.liveness) {
        setVerificationData({...verificationData, faceVerified: true});
        toast({ 
          title: 'Liveness Check Passed!', 
          description: `Photo verified (Score: ${(livenessResult.score * 100).toFixed(0)}%)`
        });
        setStep('review');
      } else {
        toast({ 
          title: 'Liveness Check Failed', 
          description: livenessResult.message || 'Please retake photo with natural expression',
          variant: 'destructive'
        });
        setLivenessPhoto(null);
      }
    }
  };

  const submitForVerification = async () => {
    if (!idDocument) {
      toast({ title: 'Please upload your ID document', variant: 'destructive' });
      return;
    }

    if (isDriver && !driversLicense) {
      toast({ title: "Please upload your driver's license", variant: 'destructive' });
      return;
    }

    if (isDriver && !vehicleCertificate) {
      toast({ title: 'Please upload vehicle certificate of fitness', variant: 'destructive' });
      return;
    }

    if (!livenessPhoto) {
      toast({ title: 'Please complete liveness check', variant: 'destructive' });
      return;
    }

    toast({ title: 'Submitting Verification...', description: 'Please wait while we process your documents' });

    try {
      // Submit to backend API
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      formData.append('user_id', user.id);
      formData.append('id_document', idDocument);
      formData.append('liveness_photo', livenessPhoto);
      
      if (driversLicense) {
        formData.append('drivers_license', driversLicense);
      }
      
      if (vehicleCertificate) {
        formData.append('vehicle_certificate', vehicleCertificate);
      }

      const response = await axios.post(
        `${getApiBaseUrl()}/api/verification/submit`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast({ 
          title: 'Verification Submitted!', 
          description: response.data.message
        });
        
        // Update user state with new verification status
        onVerificationComplete({
          status: 'pending',
          idVerified: false,
          faceVerified: false,
          documentsVerified: true,
          message: 'Documents submitted for review'
        });
        
        // Close the modal after successful submission
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        toast({ 
          title: 'Submission Failed', 
          description: response.data.message || 'Failed to submit verification',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Verification submission error:', error);
      toast({ 
        title: 'Submission Error', 
        description: error.response?.data?.detail || 'Failed to submit verification documents',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <Card className="max-w-3xl w-full shadow-2xl my-8">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-800">Verification Center</h2>
              <p className="text-sm text-gray-600">Complete verification to start {isDriver ? 'accepting' : 'booking'} rides</p>
            </div>
            <Button
              onClick={handleCancel}
              variant="ghost"
              size="sm"
              className="text-gray-500 hover:text-gray-700"
            >
              Cancel
            </Button>
          </div>

          {/* Overview Step */}
          {step === 'overview' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Why verify?</strong> Verification helps keep our community safe and builds trust between riders and drivers.
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800">Required Documents:</h3>
                
                <div className="space-y-3">
                  <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                    <FileText className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-800">National ID or Passport</p>
                      <p className="text-sm text-gray-600">Clear photo of your government-issued ID</p>
                    </div>
                  </div>

                  {isDriver && (
                    <>
                      <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                        <FileText className="w-5 h-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-800">Driver's License</p>
                          <p className="text-sm text-gray-600">Valid driver's license</p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                        <FileText className="w-5 h-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-800">Certificate of Fitness</p>
                          <p className="text-sm text-gray-600">Valid vehicle certificate (not expired)</p>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                    <Camera className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-800">Liveness Check</p>
                      <p className="text-sm text-gray-600">Take a selfie to verify your identity</p>
                    </div>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => setStep('id_upload')}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold"
              >
                Start Verification
              </Button>
            </div>
          )}

          {/* ID Upload Step */}
          {step === 'id_upload' && (
            <div className="space-y-6">
              <div>
                <Label className="text-gray-700 mb-3 block">Upload National ID or Passport *</Label>
                <label className="block cursor-pointer">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-500 transition-colors">
                    {idDocument ? (
                      <div>
                        <img src={idDocument} alt="ID" className="max-h-64 mx-auto rounded-lg mb-3" />
                        <p className="text-sm text-green-600 font-medium">ID uploaded successfully</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600 font-medium">Click to upload ID</p>
                        <p className="text-sm text-gray-400 mt-1">JPG, PNG or PDF</p>
                      </>
                    )}
                  </div>
                  <input type="file" accept="image/*,.pdf" onChange={(e) => handleFileUpload('id', e)} className="hidden" />
                </label>
              </div>

              {isDriver && (
                <>
                  <div>
                    <Label className="text-gray-700 mb-3 block">Upload Driver's License *</Label>
                    <label className="block cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-500 transition-colors">
                        {driversLicense ? (
                          <div>
                            <img src={driversLicense} alt="License" className="max-h-64 mx-auto rounded-lg mb-3" />
                            <p className="text-sm text-green-600 font-medium">License uploaded</p>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-600 font-medium">Click to upload license</p>
                          </>
                        )}
                      </div>
                      <input type="file" accept="image/*,.pdf" onChange={(e) => handleFileUpload('license', e)} className="hidden" />
                    </label>
                  </div>

                  <div>
                    <Label className="text-gray-700 mb-3 block">Upload Vehicle Certificate of Fitness *</Label>
                    <label className="block cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-500 transition-colors">
                        {vehicleCertificate ? (
                          <div>
                            <img src={vehicleCertificate} alt="Certificate" className="max-h-64 mx-auto rounded-lg mb-3" />
                            <p className="text-sm text-green-600 font-medium">Certificate uploaded</p>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-600 font-medium">Click to upload certificate</p>
                            <p className="text-sm text-gray-400 mt-1">Must be valid (not expired)</p>
                          </>
                        )}
                      </div>
                      <input type="file" accept="image/*,.pdf" onChange={(e) => handleFileUpload('certificate', e)} className="hidden" />
                    </label>
                  </div>
                </>
              )}

              <div className="flex space-x-3">
                <Button onClick={() => setStep('overview')} variant="outline" className="flex-1 py-6">
                  Back
                </Button>
                <Button 
                  onClick={() => setStep('liveness')}
                  disabled={!idDocument || (isDriver && (!driversLicense || !vehicleCertificate))}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-6 font-semibold"
                >
                  Continue to Liveness Check
                </Button>
              </div>
            </div>
          )}

          {/* Liveness Check Step */}
          {step === 'liveness' && (
            <div className="space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Instructions:</strong> Position your face in the frame and click capture. Ensure good lighting and remove glasses if possible.
                </p>
              </div>

              <div className="flex justify-center">
                {!livenessPhoto ? (
                  <div className="w-full max-w-md">
                    <Webcam
                      audio={false}
                      ref={setWebcamRef}
                      screenshotFormat="image/jpeg"
                      className="rounded-lg w-full"
                      videoConstraints={{ facingMode: 'user' }}
                    />
                  </div>
                ) : (
                  <div className="w-full max-w-md">
                    <img src={livenessPhoto} alt="Captured" className="rounded-lg w-full" />
                    <div className="mt-4 flex items-center justify-center space-x-2 text-green-600">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Photo captured successfully</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex space-x-3">
                <Button onClick={() => setStep('id_upload')} variant="outline" className="flex-1 py-6">
                  Back
                </Button>
                {!livenessPhoto ? (
                  <Button onClick={captureLiveness} className="flex-1 bg-green-600 hover:bg-green-700 text-white py-6 font-semibold">
                    Capture Photo
                  </Button>
                ) : (
                  <Button onClick={() => setLivenessPhoto(null)} variant="outline" className="flex-1 py-6">
                    Retake
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Review Step */}
          {step === 'review' && (
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-800">ID Document</span>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>

                {isDriver && (
                  <>
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-gray-600" />
                        <span className="font-medium text-gray-800">Driver's License</span>
                      </div>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-gray-600" />
                        <span className="font-medium text-gray-800">Vehicle Certificate</span>
                      </div>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                  </>
                )}

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Camera className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-800">Liveness Check</span>
                  </div>
                  {verificationData.faceVerified ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                  )}
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>Ready to submit!</strong> Your documents will be verified within 24 hours.
                </p>
              </div>

              <div className="flex space-x-3">
                <Button onClick={onClose} variant="outline" className="flex-1 py-6">
                  Cancel
                </Button>
                <Button onClick={submitForVerification} className="flex-1 bg-green-600 hover:bg-green-700 text-white py-6 font-semibold">
                  Submit for Verification
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Cancel Confirmation Dialog */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full shadow-2xl">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Cancel Verification?</h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                Your verification progress will be lost. You'll need to start over next time. Are you sure you want to cancel?
              </p>

              <div className="flex space-x-3">
                <Button
                  onClick={() => setShowCancelConfirm(false)}
                  variant="outline"
                  className="flex-1 py-6 font-semibold"
                >
                  Continue Verification
                </Button>
                <Button
                  onClick={confirmCancel}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-6 font-semibold"
                >
                  Yes, Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default VerificationCenter;